import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Truck, BarChart3,
  Settings, LogOut, Menu, X, Bell, Search, Plus, Edit, Trash2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, DollarSign,
  Archive, FileText, ChevronDown, ChevronRight, Eye, RefreshCw,
  Calendar, CreditCard, Award, User, Activity, Layers, Printer
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user } = res.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    return user;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { access_token, user } = res.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Loading Screen
const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
      <p className="text-slate-400">Loading...</p>
    </div>
  </div>
);

// Login Page
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (isRegister) {
        await register({ username, password, firstname, lastname });
      } else {
        await login(username, password);
      }
      navigate('/');
    } catch (e) {
      setError(e.response?.data?.detail || 'Authentication failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Ikras Backend</h1>
          <p className="text-slate-400 mt-2">{isRegister ? 'Create your account' : 'Sign in to your account'}</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                <input
                  type="text"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                <input
                  type="text"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              placeholder="Enter username"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              placeholder="Enter password"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold rounded-lg transition-all"
          >
            {isRegister ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-slate-400 mt-6">
          {isRegister ? 'Already have an account?' : "Don't have an account?"}
          <button
            onClick={() => setIsRegister(!isRegister)}
            className="text-blue-400 hover:text-blue-300 ml-2"
          >
            {isRegister ? 'Sign In' : 'Register'}
          </button>
        </p>
      </div>
    </div>
  );
};

// Sidebar
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: Package, label: 'Products', path: '/products' },
    { icon: ShoppingCart, label: 'Sales', path: '/sales' },
    { icon: Users, label: 'Customers', path: '/customers' },
    { icon: Truck, label: 'Suppliers', path: '/suppliers' },
    { icon: Archive, label: 'Inventory', path: '/inventory' },
    { icon: BarChart3, label: 'Reports', path: '/reports' },
  ];

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-700 transform transition-transform lg:transform-none ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-red-500 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">Ikras Backend</span>
            </div>
            <button onClick={onClose} className="lg:hidden text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {user?.firstname?.[0]}{user?.lastname?.[0]}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{user?.firstname} {user?.lastname}</p>
                <p className="text-sm text-slate-400">{user?.user_type}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 w-full px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Main Layout
const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-900 flex">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 max-w-xl mx-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <button className="relative text-slate-400 hover:text-white">
            <Bell className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">3</span>
          </button>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

// Dashboard
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed-data`);
      fetchStats();
    } catch (e) {
      console.error('Failed to seed data:', e);
    }
  };

  if (loading) return <LoadingScreen />;

  const COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400">Welcome back! Here's your business overview.</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Today's Sales"
          value={`KES ${(stats?.today_sales || 0).toLocaleString()}`}
          subtitle={`${stats?.today_sales_count || 0} transactions`}
          icon={DollarSign}
          color="blue"
        />
        <StatCard
          title="Monthly Revenue"
          value={`KES ${(stats?.month_sales || 0).toLocaleString()}`}
          subtitle={`Profit: KES ${(stats?.month_profit || 0).toLocaleString()}`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Total Products"
          value={stats?.total_products || 0}
          subtitle={`${stats?.low_stock_count || 0} low stock`}
          icon={Package}
          color="purple"
        />
        <StatCard
          title="Customers"
          value={stats?.total_customers || 0}
          subtitle={`${stats?.total_suppliers || 0} suppliers`}
          icon={Users}
          color="orange"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Sales Trend (Last 7 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.daily_sales || []}>
                <defs>
                  <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="_id" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Area type="monotone" dataKey="total" stroke="#3B82F6" fill="url(#salesGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Sales */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Sales by Category</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats?.category_sales || []}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="total"
                  nameKey="_id"
                  label={({ _id, percent }) => `${_id} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {(stats?.category_sales || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Products & Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Selling Products */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Top Selling Products</h3>
          <div className="space-y-3">
            {(stats?.top_products || []).map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center font-semibold">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-white font-medium">{product.description}</p>
                    <p className="text-sm text-slate-400">{product.total_qty} units sold</p>
                  </div>
                </div>
                <span className="text-green-400 font-semibold">KES {product.total_amount?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Low Stock Alert</h3>
            <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
              {stats?.low_stock_count || 0} items
            </span>
          </div>
          <div className="space-y-3">
            <LowStockList />
          </div>
        </div>
      </div>
    </div>
  );
};

const LowStockList = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchLowStock = async () => {
      try {
        const res = await axios.get(`${API}/products/low-stock`);
        setProducts(res.data.products?.slice(0, 5) || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchLowStock();
  }, []);

  return (
    <>
      {products.map((product, index) => (
        <div key={index} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-white font-medium">{product.description}</p>
              <p className="text-sm text-slate-400">Code: {product.product_code}</p>
            </div>
          </div>
          <span className="text-red-400 font-semibold">{product.qty_remain} left</span>
        </div>
      ))}
      {products.length === 0 && (
        <p className="text-slate-400 text-center py-4">No low stock items</p>
      )}
    </>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
        </div>
        <div className={`w-12 h-12 bg-gradient-to-r ${colorClasses[color]} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
};

// Products Page
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const limit = 20;

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [page, search, category]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`, {
        params: { skip: page * limit, limit, search, category }
      });
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/products/categories`);
      setCategories(res.data.categories);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (productCode) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productCode}`);
      fetchProducts();
    } catch (e) {
      alert('Failed to delete product');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-slate-400">Manage your product inventory</p>
        </div>
        <button
          onClick={() => { setEditProduct(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(0); }}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Products Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Description</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Category</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-300">Cost</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-300">Price</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-300">Stock</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No products found</td></tr>
              ) : (
                products.map((product) => (
                  <tr key={product.product_code} className="hover:bg-slate-700/30">
                    <td className="px-4 py-3 text-white font-medium">{product.product_code}</td>
                    <td className="px-4 py-3 text-slate-300">{product.description}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                        {product.category || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">KES {product.cost_price?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-green-400">KES {product.selling_price?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${product.qty_remain <= product.min_stock ? 'text-red-400' : 'text-white'}`}>
                        {product.qty_remain}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => { setEditProduct(product); setShowModal(true); }}
                          className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.product_code)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
          <p className="text-sm text-slate-400">Showing {products.length} of {total} products</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1 bg-slate-700 text-white rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-3 py-1 bg-slate-700 text-white rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Product Modal */}
      {showModal && (
        <ProductModal
          product={editProduct}
          categories={categories}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchProducts(); }}
        />
      )}
    </div>
  );
};

const ProductModal = ({ product, categories, onClose, onSave }) => {
  const [form, setForm] = useState(product || {
    product_code: '',
    description: '',
    category: '',
    unit: 'PCS',
    cost_price: 0,
    selling_price: 0,
    qty_remain: 0,
    min_stock: 10,
    vat: 16,
    barcode: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (product) {
        await axios.put(`${API}/products/${product.product_code}`, form);
      } else {
        await axios.post(`${API}/products`, form);
      }
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save product');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">
            {product ? 'Edit Product' : 'Add Product'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Product Code</label>
              <input
                type="text"
                value={form.product_code}
                onChange={(e) => setForm({ ...form, product_code: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                required
                disabled={!!product}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Barcode</label>
              <input
                type="text"
                value={form.barcode}
                onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                <option value="BEVERAGES">BEVERAGES</option>
                <option value="SNACKS/SWEETS">SNACKS/SWEETS</option>
                <option value="HOUSEHOLD">HOUSEHOLD</option>
                <option value="SANITARY">SANITARY</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Unit</label>
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="PCS">PCS</option>
                <option value="KG">KG</option>
                <option value="LTR">LTR</option>
                <option value="BOX">BOX</option>
                <option value="PACK">PACK</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Cost Price (KES)</label>
              <input
                type="number"
                value={form.cost_price}
                onChange={(e) => setForm({ ...form, cost_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Selling Price (KES)</label>
              <input
                type="number"
                value={form.selling_price}
                onChange={(e) => setForm({ ...form, selling_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Stock Qty</label>
              <input
                type="number"
                value={form.qty_remain}
                onChange={(e) => setForm({ ...form, qty_remain: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Min Stock</label>
              <input
                type="number"
                value={form.min_stock}
                onChange={(e) => setForm({ ...form, min_stock: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">VAT %</label>
              <input
                type="number"
                value={form.vat}
                onChange={(e) => setForm({ ...form, vat: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                min="0"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500"
            >
              {product ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Sales Page
const SalesPage = () => {
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNewSale, setShowNewSale] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchSales();
  }, [page]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales`, {
        params: { skip: page * limit, limit }
      });
      setSales(res.data.sales);
      setTotal(res.data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Sales</h1>
          <p className="text-slate-400">View and manage sales transactions</p>
        </div>
        <button
          onClick={() => setShowNewSale(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          New Sale
        </button>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Invoice</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Date</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-300">Items</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-300">Total</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Payment</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : sales.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No sales found</td></tr>
              ) : (
                sales.map((sale) => (
                  <tr key={sale.invoice_no} className="hover:bg-slate-700/30">
                    <td className="px-4 py-3 text-blue-400 font-medium">{sale.invoice_no}</td>
                    <td className="px-4 py-3 text-white">{sale.customer}</td>
                    <td className="px-4 py-3 text-slate-300">
                      {sale.created_at ? format(new Date(sale.created_at), 'dd MMM yyyy HH:mm') : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">{sale.items_count}</td>
                    <td className="px-4 py-3 text-right text-green-400 font-semibold">
                      KES {sale.total_amount?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                        {sale.payment_mode}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <button className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
          <p className="text-sm text-slate-400">Showing {sales.length} of {total} sales</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1 bg-slate-700 text-white rounded hover:bg-slate-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-3 py-1 bg-slate-700 text-white rounded hover:bg-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showNewSale && (
        <NewSaleModal onClose={() => setShowNewSale(false)} onSave={() => { setShowNewSale(false); fetchSales(); }} />
      )}
    </div>
  );
};

const NewSaleModal = ({ onClose, onSave }) => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [customer, setCustomer] = useState('Walk-in Customer');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [amountPaid, setAmountPaid] = useState(0);

  useEffect(() => {
    if (search.length >= 2) {
      fetchProducts();
    }
  }, [search]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, { params: { search, limit: 10 } });
      setProducts(res.data.products);
    } catch (e) {
      console.error(e);
    }
  };

  const addItem = (product) => {
    const existing = items.find(i => i.product_code === product.product_code);
    if (existing) {
      setItems(items.map(i => 
        i.product_code === product.product_code ? { ...i, qty: i.qty + 1 } : i
      ));
    } else {
      setItems([...items, {
        product_code: product.product_code,
        description: product.description,
        qty: 1,
        price: product.selling_price,
        unit: product.unit,
        vat: product.vat || 0,
        cost_price: product.cost_price
      }]);
    }
    setSearch('');
    setProducts([]);
  };

  const updateQty = (index, qty) => {
    if (qty <= 0) {
      setItems(items.filter((_, i) => i !== index));
    } else {
      setItems(items.map((item, i) => i === index ? { ...item, qty } : item));
    }
  };

  const total = items.reduce((sum, item) => {
    const itemTotal = item.qty * item.price;
    const vat = itemTotal * (item.vat / 100);
    return sum + itemTotal + vat;
  }, 0);

  const handleSubmit = async () => {
    if (items.length === 0) {
      alert('Please add at least one item');
      return;
    }
    try {
      await axios.post(`${API}/sales`, {
        customer,
        items,
        payment_mode: paymentMode,
        amount_paid: amountPaid || total
      });
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create sale');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">New Sale</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Search Products */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
            />
            {products.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                {products.map((product) => (
                  <button
                    key={product.product_code}
                    onClick={() => addItem(product)}
                    className="w-full px-4 py-2 text-left text-white hover:bg-slate-600 flex items-center justify-between"
                  >
                    <span>{product.description}</span>
                    <span className="text-green-400">KES {product.selling_price}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Items List */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Cart Items</h3>
            {items.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No items added</p>
            ) : (
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-slate-600/50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-white font-medium">{item.description}</p>
                      <p className="text-sm text-slate-400">KES {item.price} x {item.qty}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQty(index, item.qty - 1)}
                        className="w-8 h-8 bg-slate-500 text-white rounded hover:bg-slate-400"
                      >
                        -
                      </button>
                      <span className="w-8 text-center text-white">{item.qty}</span>
                      <button
                        onClick={() => updateQty(index, item.qty + 1)}
                        className="w-8 h-8 bg-slate-500 text-white rounded hover:bg-slate-400"
                      >
                        +
                      </button>
                      <span className="ml-2 text-green-400 font-semibold w-24 text-right">
                        KES {(item.qty * item.price).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customer & Payment */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Customer</label>
              <input
                type="text"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Payment Mode</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="Cash">Cash</option>
                <option value="M-Pesa">M-Pesa</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold text-white">Total</span>
            <span className="text-2xl font-bold text-green-400">KES {total.toLocaleString()}</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500"
            >
              Complete Sale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Generic CRUD Page Component
const CrudPage = ({ title, subtitle, apiEndpoint, columns, formFields }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [page, search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/${apiEndpoint}`, {
        params: { skip: page * limit, limit, search }
      });
      setData(res.data[apiEndpoint] || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/${apiEndpoint}/${id}`);
      fetchData();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{title}</h1>
          <p className="text-slate-400">{subtitle}</p>
        </div>
        <button
          onClick={() => { setEditItem(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          Add {title.slice(0, -1)}
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder={`Search ${title.toLowerCase()}...`}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
        />
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className={`px-4 py-3 text-${col.align || 'left'} text-sm font-semibold text-slate-300`}>
                    {col.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-400">No data found</td></tr>
              ) : (
                data.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-700/30">
                    {columns.map((col) => (
                      <td key={col.key} className={`px-4 py-3 text-${col.align || 'left'} ${col.className || 'text-slate-300'}`}>
                        {col.render ? col.render(item[col.key], item) : item[col.key]}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => { setEditItem(item); setShowModal(true); }}
                          className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
          <p className="text-sm text-slate-400">Showing {data.length} of {total}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1 bg-slate-700 text-white rounded hover:bg-slate-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-3 py-1 bg-slate-700 text-white rounded hover:bg-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <GenericModal
          item={editItem}
          fields={formFields}
          apiEndpoint={apiEndpoint}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchData(); }}
        />
      )}
    </div>
  );
};

const GenericModal = ({ item, fields, apiEndpoint, onClose, onSave }) => {
  const [form, setForm] = useState(item || fields.reduce((acc, f) => ({ ...acc, [f.name]: f.default || '' }), {}));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (item) {
        await axios.put(`${API}/${apiEndpoint}/${item.id}`, form);
      } else {
        await axios.post(`${API}/${apiEndpoint}`, form);
      }
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">
            {item ? 'Edit' : 'Add'} Item
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {fields.map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-slate-300 mb-1">{field.label}</label>
              <input
                type={field.type || 'text'}
                value={form[field.name] || ''}
                onChange={(e) => setForm({ ...form, [field.name]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                required={field.required}
              />
            </div>
          ))}
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">
              {item ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Customers Page
const CustomersPage = () => (
  <CrudPage
    title="Customers"
    subtitle="Manage your customer database"
    apiEndpoint="customers"
    columns={[
      { key: 'name', label: 'Name', className: 'text-white font-medium' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'address', label: 'Address' },
      { key: 'balance', label: 'Balance', align: 'right', render: (v) => <span className={v > 0 ? 'text-red-400' : 'text-green-400'}>KES {v?.toLocaleString()}</span> },
    ]}
    formFields={[
      { name: 'name', label: 'Name', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'address', label: 'Address' },
      { name: 'balance', label: 'Balance', type: 'number', default: 0 },
    ]}
  />
);

// Suppliers Page
const SuppliersPage = () => (
  <CrudPage
    title="Suppliers"
    subtitle="Manage your supplier relationships"
    apiEndpoint="suppliers"
    columns={[
      { key: 'name', label: 'Name', className: 'text-white font-medium' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'address', label: 'Address' },
      { key: 'balance', label: 'Balance', align: 'right', render: (v) => <span className={v > 0 ? 'text-red-400' : 'text-green-400'}>KES {v?.toLocaleString()}</span> },
    ]}
    formFields={[
      { name: 'name', label: 'Name', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'address', label: 'Address' },
      { name: 'balance', label: 'Balance', type: 'number', default: 0 },
    ]}
  />
);

// Inventory Page
const InventoryPage = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReceive, setShowReceive] = useState(false);

  useEffect(() => {
    fetchMovements();
  }, []);

  const fetchMovements = async () => {
    try {
      const res = await axios.get(`${API}/stock/movements`, { params: { limit: 50 } });
      setMovements(res.data.movements);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Inventory</h1>
          <p className="text-slate-400">Track stock movements and receive inventory</p>
        </div>
        <button
          onClick={() => setShowReceive(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          Receive Stock
        </button>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Product</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Type</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-300">Qty</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">Reference</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-300">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : movements.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">No movements found</td></tr>
              ) : (
                movements.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-700/30">
                    <td className="px-4 py-3 text-slate-300">
                      {m.created_at ? format(new Date(m.created_at), 'dd MMM HH:mm') : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-white">{m.description}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        m.movement_type === 'RECEIVE' ? 'bg-green-500/20 text-green-400' :
                        m.movement_type === 'SALE' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-orange-500/20 text-orange-400'
                      }`}>
                        {m.movement_type}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${m.qty > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {m.qty > 0 ? '+' : ''}{m.qty}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{m.reference || '-'}</td>
                    <td className="px-4 py-3 text-slate-300">{m.created_by}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showReceive && (
        <ReceiveStockModal onClose={() => setShowReceive(false)} onSave={() => { setShowReceive(false); fetchMovements(); }} />
      )}
    </div>
  );
};

const ReceiveStockModal = ({ onClose, onSave }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    product_code: '',
    description: '',
    quantity: 0,
    cost_price: 0,
    supplier: '',
    invoice_no: ''
  });

  useEffect(() => {
    if (search.length >= 2) {
      fetchProducts();
    }
  }, [search]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, { params: { search, limit: 10 } });
      setProducts(res.data.products);
    } catch (e) {
      console.error(e);
    }
  };

  const selectProduct = (product) => {
    setForm({
      ...form,
      product_code: product.product_code,
      description: product.description,
      cost_price: product.cost_price
    });
    setSearch('');
    setProducts([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/stock/receive`, form);
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to receive stock');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">Receive Stock</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-slate-300 mb-1">Product</label>
            <input
              type="text"
              placeholder="Search product..."
              value={form.description || search}
              onChange={(e) => { setSearch(e.target.value); setForm({ ...form, description: '', product_code: '' }); }}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              required
            />
            {products.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg z-10">
                {products.map((p) => (
                  <button
                    key={p.product_code}
                    type="button"
                    onClick={() => selectProduct(p)}
                    className="w-full px-4 py-2 text-left text-white hover:bg-slate-600"
                  >
                    {p.description}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Quantity</label>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                min="1"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Cost Price</label>
              <input
                type="number"
                value={form.cost_price}
                onChange={(e) => setForm({ ...form, cost_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Supplier</label>
            <input
              type="text"
              value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Invoice No</label>
            <input
              type="text"
              value={form.invoice_no}
              onChange={(e) => setForm({ ...form, invoice_no: e.target.value })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500">
              Receive
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reports Page
const ReportsPage = () => {
  const [activeReport, setActiveReport] = useState('sales');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    date: new Date().toISOString().split('T')[0],
    category: '',
    productCode: '',
    limit: 50,
    sortBy: 'qty',
    groupBy: 'product'
  });

  const reports = [
    { id: 'sales', name: 'Sales Report', icon: TrendingUp, category: 'Sales' },
    { id: 'daily-sales-summary', name: 'Daily Sales Summary', icon: Calendar, category: 'Sales' },
    { id: 'products-sold', name: 'Products Sold Report', icon: ShoppingCart, category: 'Sales' },
    { id: 'sales-by-payment-mode', name: 'Sales by Payment Mode', icon: CreditCard, category: 'Sales' },
    { id: 'sales-by-customer', name: 'Sales by Customer', icon: Users, category: 'Sales' },
    { id: 'top-selling-products', name: 'Top Selling Products', icon: Award, category: 'Sales' },
    { id: 'sales-comparison', name: 'Sales Comparison', icon: BarChart3, category: 'Sales' },
    { id: 'user-performance', name: 'User Performance', icon: User, category: 'Performance' },
    { id: 'user-activity', name: 'User Activity', icon: Activity, category: 'Performance' },
    { id: 'inventory', name: 'Inventory Report', icon: Package, category: 'Inventory' },
    { id: 'stock-valuation', name: 'Stock Valuation', icon: Archive, category: 'Inventory' },
    { id: 'stock-movement-history', name: 'Stock Movement History', icon: RefreshCw, category: 'Inventory' },
    { id: 'category-stock', name: 'Category Stock', icon: Layers, category: 'Inventory' },
    { id: 'profit', name: 'Profit Report', icon: DollarSign, category: 'Financial' }
  ];

  useEffect(() => {
    fetchReport();
  }, [activeReport]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${API}/reports/${activeReport}`;
      const params = new URLSearchParams();
      
      if (activeReport === 'daily-sales-summary' && filters.date) {
        params.append('date', filters.date);
      }
      if (activeReport === 'products-sold') {
        if (filters.date && !filters.fromDate && !filters.toDate) {
          params.append('date', filters.date);
        } else {
          if (filters.fromDate) params.append('from_date', filters.fromDate);
          if (filters.toDate) params.append('to_date', filters.toDate);
        }
        if (filters.category) params.append('category', filters.category);
        params.append('group_by', filters.groupBy);
      }
      if (['sales', 'sales-by-payment-mode', 'sales-by-customer', 'top-selling-products', 'user-performance', 'user-activity', 'stock-movement-history', 'profit'].includes(activeReport)) {
        if (filters.fromDate) params.append('from_date', filters.fromDate);
        if (filters.toDate) params.append('to_date', filters.toDate);
      }
      if (activeReport === 'top-selling-products') {
        params.append('limit', filters.limit);
        params.append('sort_by', filters.sortBy);
      }
      if (activeReport === 'stock-valuation' && filters.category) {
        params.append('category', filters.category);
      }
      if (activeReport === 'stock-movement-history' && filters.productCode) {
        params.append('product_code', filters.productCode);
      }
      
      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;
      
      const res = await axios.get(url);
      setReportData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('report-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${reports.find(r => r.id === activeReport)?.name || 'Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #000; }
            h1 { color: #1e3a8a; border-bottom: 2px solid #1e3a8a; padding-bottom: 10px; }
            h2 { color: #1e40af; margin-top: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
            th { background-color: #1e3a8a; color: white; }
            tr:nth-child(even) { background-color: #f3f4f6; }
            .summary-box { background: #eff6ff; padding: 15px; border-radius: 8px; margin: 15px 0; }
            .stat { display: inline-block; margin-right: 30px; }
            .stat-value { font-size: 24px; font-weight: bold; color: #1e3a8a; }
            .stat-label { color: #6b7280; font-size: 14px; }
            .print-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; color: #1e3a8a; }
            .report-date { color: #6b7280; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="company-name">Teti Pharmacy Limited</div>
            <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
          </div>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const renderFilters = () => {
    const showDateRange = ['sales', 'sales-by-payment-mode', 'sales-by-customer', 'top-selling-products', 'user-performance', 'user-activity', 'stock-movement-history', 'profit'].includes(activeReport);
    const showSingleDate = activeReport === 'daily-sales-summary';
    const showProductsSoldFilters = activeReport === 'products-sold';
    
    return (
      <div className="flex flex-wrap gap-3 items-end mb-4">
        {showSingleDate && (
          <div>
            <label className="block text-sm text-slate-400 mb-1">Date</label>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({...filters, date: e.target.value})}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
            />
          </div>
        )}
        {showProductsSoldFilters && (
          <>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Single Date</label>
              <input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters({...filters, date: e.target.value, fromDate: '', toDate: ''})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div className="text-slate-500 self-center">OR</div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters({...filters, fromDate: e.target.value, date: ''})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters({...filters, toDate: e.target.value, date: ''})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Group By</label>
              <select
                value={filters.groupBy}
                onChange={(e) => setFilters({...filters, groupBy: e.target.value})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="product">Product</option>
                <option value="category">Category</option>
                <option value="date">Date</option>
              </select>
            </div>
          </>
        )}
        {showDateRange && (
          <>
            <div>
              <label className="block text-sm text-slate-400 mb-1">From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters({...filters, fromDate: e.target.value})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters({...filters, toDate: e.target.value})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              />
            </div>
          </>
        )}
        {activeReport === 'top-selling-products' && (
          <div>
            <label className="block text-sm text-slate-400 mb-1">Sort By</label>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="qty">Quantity Sold</option>
              <option value="revenue">Revenue</option>
            </select>
          </div>
        )}
        <button
          onClick={fetchReport}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Generate
        </button>
        <button
          onClick={handlePrint}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <Printer className="w-4 h-4" /> Print Report
        </button>
      </div>
    );
  };

  const renderSalesReport = () => (
    <div className="space-y-6">
      <div className="summary-box grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatCard title="Total Sales" value={`KES ${reportData.summary?.total_sales?.toLocaleString() || 0}`} icon={DollarSign} color="blue" subtitle={`${reportData.summary?.transaction_count || 0} transactions`} />
        <StatCard title="Total Profit" value={`KES ${reportData.summary?.total_profit?.toLocaleString() || 0}`} icon={TrendingUp} color="green" subtitle="Net profit" />
        <StatCard title="VAT Collected" value={`KES ${reportData.summary?.total_vat?.toLocaleString() || 0}`} icon={FileText} color="purple" subtitle="Tax collected" />
        <StatCard title="Avg Transaction" value={`KES ${reportData.summary?.transaction_count ? Math.round(reportData.summary?.total_sales / reportData.summary?.transaction_count).toLocaleString() : 0}`} icon={BarChart3} color="orange" subtitle="Per sale" />
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Sales Trend</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportData.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="_id" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
              <Bar dataKey="total_sales" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const renderDailySalesSummary = () => (
    <div className="space-y-6">
      <div className="summary-box grid grid-cols-1 sm:grid-cols-5 gap-4">
        <StatCard title="Total Sales" value={`KES ${reportData.summary?.total_sales?.toLocaleString() || 0}`} icon={DollarSign} color="blue" />
        <StatCard title="Transactions" value={reportData.summary?.total_transactions || 0} icon={ShoppingCart} color="green" />
        <StatCard title="Profit" value={`KES ${reportData.summary?.total_profit?.toLocaleString() || 0}`} icon={TrendingUp} color="purple" />
        <StatCard title="VAT" value={`KES ${reportData.summary?.total_vat?.toLocaleString() || 0}`} icon={FileText} color="orange" />
        <StatCard title="Avg Transaction" value={`KES ${Math.round(reportData.summary?.avg_transaction || 0).toLocaleString()}`} icon={BarChart3} color="red" />
      </div>
      
      {reportData.hourly_breakdown?.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Hourly Breakdown</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={reportData.hourly_breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="hour" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                <Bar dataKey="total_sales" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Payment Breakdown</h3>
          <div className="space-y-3">
            {(reportData.payment_breakdown || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  <span className="text-white">{p.payment_mode}</span>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-semibold">KES {p.total_amount?.toLocaleString()}</p>
                  <p className="text-sm text-slate-400">{p.transactions} transactions</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Top Products</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {(reportData.top_products || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">{p.description}</p>
                  <p className="text-sm text-slate-400">{p.qty_sold} units sold</p>
                </div>
                <span className="text-green-400 font-semibold">KES {p.total_sales?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSalesByPaymentMode = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Total Sales: KES {reportData.total_sales?.toLocaleString()}</h3>
        <p className="text-slate-400 mb-4">{reportData.from_date} - {reportData.to_date}</p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">Payment Mode</th>
                <th className="text-right py-3 px-4 text-slate-300">Transactions</th>
                <th className="text-right py-3 px-4 text-slate-300">Total Amount</th>
                <th className="text-right py-3 px-4 text-slate-300">Avg Transaction</th>
                <th className="text-right py-3 px-4 text-slate-300">%</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.payment_modes || []).map((p, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-white font-medium">{p.payment_mode}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{p.transaction_count?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400 font-semibold">KES {p.total_amount?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-slate-300">KES {Math.round(p.avg_transaction)?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-blue-400">{p.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSalesByCustomer = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Top Customers</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">#</th>
                <th className="text-left py-3 px-4 text-slate-300">Customer</th>
                <th className="text-right py-3 px-4 text-slate-300">Transactions</th>
                <th className="text-right py-3 px-4 text-slate-300">Total Purchases</th>
                <th className="text-right py-3 px-4 text-slate-300">Avg Purchase</th>
                <th className="text-right py-3 px-4 text-slate-300">Last Purchase</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.customers || []).map((c, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-slate-400">{i + 1}</td>
                  <td className="py-3 px-4 text-white font-medium">{c.customer}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{c.transaction_count?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400 font-semibold">KES {c.total_purchases?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-slate-300">KES {Math.round(c.avg_purchase)?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-slate-400">{c.last_purchase}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTopSellingProducts = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Top Selling Products - Sorted by {reportData.sort_by}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">#</th>
                <th className="text-left py-3 px-4 text-slate-300">Product</th>
                <th className="text-left py-3 px-4 text-slate-300">Category</th>
                <th className="text-right py-3 px-4 text-slate-300">Qty Sold</th>
                <th className="text-right py-3 px-4 text-slate-300">Revenue</th>
                <th className="text-right py-3 px-4 text-slate-300">Profit</th>
                <th className="text-right py-3 px-4 text-slate-300">Margin</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.products || []).map((p, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-slate-400">{i + 1}</td>
                  <td className="py-3 px-4 text-white font-medium">{p.description}</td>
                  <td className="py-3 px-4 text-slate-400">{p.category}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{p.total_qty?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-blue-400">KES {p.total_revenue?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400">KES {p.total_profit?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-purple-400">{p.profit_margin}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderProductsSold = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard title="Products Sold" value={reportData.totals?.unique_products || 0} icon={Package} color="blue" subtitle="Unique items" />
        <StatCard title="Total Qty" value={reportData.totals?.total_qty?.toLocaleString() || 0} icon={ShoppingCart} color="green" subtitle="Units sold" />
        <StatCard title="Revenue" value={`KES ${(reportData.totals?.total_revenue || 0).toLocaleString()}`} icon={DollarSign} color="purple" subtitle="Total sales" />
        <StatCard title="Profit" value={`KES ${(reportData.totals?.total_profit || 0).toLocaleString()}`} icon={TrendingUp} color="orange" subtitle="Net profit" />
        <StatCard title="Margin" value={`${reportData.totals?.profit_margin || 0}%`} icon={BarChart3} color="red" subtitle="Profit margin" />
      </div>

      {/* Detailed Summary */}
      <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-3">Report Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-slate-400">Date Range:</span> <span className="text-white font-medium">{reportData.date_range}</span></div>
          <div><span className="text-slate-400">Group By:</span> <span className="text-white font-medium capitalize">{reportData.group_by}</span></div>
          <div><span className="text-slate-400">Invoices:</span> <span className="text-white font-medium">{reportData.totals?.total_invoices?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Line Items:</span> <span className="text-white font-medium">{reportData.totals?.total_line_items?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Total Cost:</span> <span className="text-yellow-400 font-medium">KES {reportData.totals?.total_cost?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Total VAT:</span> <span className="text-purple-400 font-medium">KES {reportData.totals?.total_vat?.toLocaleString()}</span></div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          {reportData.group_by === 'product' ? 'Products Sold Details' : 
           reportData.group_by === 'category' ? 'Sales by Category' : 'Sales by Date'}
        </h3>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-slate-800">
              <tr className="border-b border-slate-700">
                {reportData.group_by === 'product' && (
                  <>
                    <th className="text-left py-3 px-4 text-slate-300">Product Code</th>
                    <th className="text-left py-3 px-4 text-slate-300">Description</th>
                    <th className="text-left py-3 px-4 text-slate-300">Category</th>
                    <th className="text-right py-3 px-4 text-slate-300">Qty Sold</th>
                    <th className="text-right py-3 px-4 text-slate-300">Avg Price</th>
                    <th className="text-right py-3 px-4 text-slate-300">Revenue</th>
                    <th className="text-right py-3 px-4 text-slate-300">Cost</th>
                    <th className="text-right py-3 px-4 text-slate-300">Profit</th>
                    <th className="text-right py-3 px-4 text-slate-300">Margin</th>
                  </>
                )}
                {reportData.group_by === 'category' && (
                  <>
                    <th className="text-left py-3 px-4 text-slate-300">Category</th>
                    <th className="text-right py-3 px-4 text-slate-300">Products</th>
                    <th className="text-right py-3 px-4 text-slate-300">Qty Sold</th>
                    <th className="text-right py-3 px-4 text-slate-300">Revenue</th>
                    <th className="text-right py-3 px-4 text-slate-300">Cost</th>
                    <th className="text-right py-3 px-4 text-slate-300">Profit</th>
                    <th className="text-right py-3 px-4 text-slate-300">VAT</th>
                    <th className="text-right py-3 px-4 text-slate-300">Margin</th>
                  </>
                )}
                {reportData.group_by === 'date' && (
                  <>
                    <th className="text-left py-3 px-4 text-slate-300">Date</th>
                    <th className="text-right py-3 px-4 text-slate-300">Products</th>
                    <th className="text-right py-3 px-4 text-slate-300">Qty Sold</th>
                    <th className="text-right py-3 px-4 text-slate-300">Revenue</th>
                    <th className="text-right py-3 px-4 text-slate-300">Cost</th>
                    <th className="text-right py-3 px-4 text-slate-300">Profit</th>
                    <th className="text-right py-3 px-4 text-slate-300">Transactions</th>
                    <th className="text-right py-3 px-4 text-slate-300">Margin</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(reportData.items || []).map((item, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  {reportData.group_by === 'product' && (
                    <>
                      <td className="py-3 px-4 text-slate-400 font-mono text-sm">{item.product_code}</td>
                      <td className="py-3 px-4 text-white font-medium">{item.description}</td>
                      <td className="py-3 px-4 text-slate-400">{item.category}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{item.total_qty?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-slate-300">KES {Math.round(item.avg_price)?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-blue-400 font-semibold">KES {item.total_revenue?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-yellow-400">KES {item.total_cost?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-green-400 font-semibold">KES {item.total_profit?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-purple-400">{item.profit_margin}%</td>
                    </>
                  )}
                  {reportData.group_by === 'category' && (
                    <>
                      <td className="py-3 px-4 text-white font-medium">{item.category}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{item.products_count}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{item.total_qty?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-blue-400 font-semibold">KES {item.total_revenue?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-yellow-400">KES {item.total_cost?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-green-400 font-semibold">KES {item.total_profit?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-purple-400">KES {item.total_vat?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-purple-400">{item.profit_margin}%</td>
                    </>
                  )}
                  {reportData.group_by === 'date' && (
                    <>
                      <td className="py-3 px-4 text-white font-medium">{item.sale_date}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{item.products_count}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{item.total_qty?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-blue-400 font-semibold">KES {item.total_revenue?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-yellow-400">KES {item.total_cost?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-green-400 font-semibold">KES {item.total_profit?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{item.transactions}</td>
                      <td className="py-3 px-4 text-right text-purple-400">{item.profit_margin}%</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
            {/* Table Footer with Totals */}
            <tfoot className="bg-slate-700/50 sticky bottom-0">
              <tr className="font-semibold">
                {reportData.group_by === 'product' && (
                  <>
                    <td className="py-3 px-4 text-white" colSpan="3">TOTALS</td>
                    <td className="py-3 px-4 text-right text-white">{reportData.totals?.total_qty?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-slate-300">-</td>
                    <td className="py-3 px-4 text-right text-blue-400">KES {reportData.totals?.total_revenue?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-yellow-400">KES {reportData.totals?.total_cost?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-green-400">KES {reportData.totals?.total_profit?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-purple-400">{reportData.totals?.profit_margin}%</td>
                  </>
                )}
                {reportData.group_by === 'category' && (
                  <>
                    <td className="py-3 px-4 text-white">TOTALS</td>
                    <td className="py-3 px-4 text-right text-white">{reportData.totals?.unique_products}</td>
                    <td className="py-3 px-4 text-right text-white">{reportData.totals?.total_qty?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-blue-400">KES {reportData.totals?.total_revenue?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-yellow-400">KES {reportData.totals?.total_cost?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-green-400">KES {reportData.totals?.total_profit?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-purple-400">KES {reportData.totals?.total_vat?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-purple-400">{reportData.totals?.profit_margin}%</td>
                  </>
                )}
                {reportData.group_by === 'date' && (
                  <>
                    <td className="py-3 px-4 text-white">TOTALS</td>
                    <td className="py-3 px-4 text-right text-white">{reportData.totals?.unique_products}</td>
                    <td className="py-3 px-4 text-right text-white">{reportData.totals?.total_qty?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-blue-400">KES {reportData.totals?.total_revenue?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-yellow-400">KES {reportData.totals?.total_cost?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-green-400">KES {reportData.totals?.total_profit?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-slate-300">{reportData.totals?.total_invoices}</td>
                    <td className="py-3 px-4 text-right text-purple-400">{reportData.totals?.profit_margin}%</td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );

  const renderUserPerformance = () => (
    <div className="space-y-6">
      <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-4 mb-4">
        <p className="text-blue-300">Total Sales by All Users: <span className="font-bold text-white">KES {reportData.total_sales?.toLocaleString()}</span></p>
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">User Sales Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">User</th>
                <th className="text-right py-3 px-4 text-slate-300">Transactions</th>
                <th className="text-right py-3 px-4 text-slate-300">Total Sales</th>
                <th className="text-right py-3 px-4 text-slate-300">Avg Transaction</th>
                <th className="text-right py-3 px-4 text-slate-300">Contribution</th>
                <th className="text-right py-3 px-4 text-slate-300">First Sale</th>
                <th className="text-right py-3 px-4 text-slate-300">Last Sale</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.users || []).map((u, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-white font-medium">{u.user}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{u.transaction_count?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400 font-semibold">KES {u.total_sales?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-slate-300">KES {Math.round(u.avg_transaction)?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-blue-400">{u.contribution_pct}%</td>
                  <td className="py-3 px-4 text-right text-slate-400">{u.first_sale}</td>
                  <td className="py-3 px-4 text-right text-slate-400">{u.last_sale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderUserActivity = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">User Activity Summary</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">User</th>
                <th className="text-right py-3 px-4 text-slate-300">Sales Count</th>
                <th className="text-right py-3 px-4 text-slate-300">Sales Amount</th>
                <th className="text-right py-3 px-4 text-slate-300">Stock Receives</th>
                <th className="text-right py-3 px-4 text-slate-300">Products Added</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.activities || []).map((a, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-white font-medium">{a.user}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{a.sales_count?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400 font-semibold">KES {a.sales_amount?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-blue-400">{a.stock_receives}</td>
                  <td className="py-3 px-4 text-right text-purple-400">{a.products_added}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderInventoryReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatCard title="Total Products" value={reportData.total_products} icon={Package} color="blue" subtitle="In catalog" />
        <StatCard title="Stock Value" value={`KES ${reportData.total_stock_value?.toLocaleString() || 0}`} icon={Archive} color="green" subtitle="At cost" />
        <StatCard title="Retail Value" value={`KES ${reportData.total_retail_value?.toLocaleString() || 0}`} icon={DollarSign} color="purple" subtitle="Selling price" />
        <StatCard title="Low Stock" value={reportData.low_stock_count} icon={AlertTriangle} color="red" subtitle="Need reorder" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Category Breakdown</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {Object.entries(reportData.category_breakdown || {}).map(([cat, stats]) => (
              <div key={cat} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">{cat}</p>
                  <p className="text-sm text-slate-400">{stats.count} products</p>
                </div>
                <span className="text-green-400 font-semibold">KES {stats.stock_value?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Low Stock Products</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {(reportData.low_stock_products || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-white font-medium">{p.description}</p>
                    <p className="text-sm text-slate-400">Min: {p.min_stock}</p>
                  </div>
                </div>
                <span className="text-red-400 font-semibold">{p.qty_remain} left</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStockValuation = () => (
    <div className="space-y-6">
      <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><span className="text-slate-400">Total Qty:</span> <span className="font-bold text-white">{reportData.totals?.total_qty?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Cost Value:</span> <span className="font-bold text-green-400">KES {reportData.totals?.cost_value?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Retail Value:</span> <span className="font-bold text-blue-400">KES {reportData.totals?.retail_value?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Potential Profit:</span> <span className="font-bold text-purple-400">KES {reportData.totals?.potential_profit?.toLocaleString()}</span></div>
        </div>
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Stock Valuation Details</h3>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-slate-800">
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">Product</th>
                <th className="text-left py-3 px-4 text-slate-300">Category</th>
                <th className="text-right py-3 px-4 text-slate-300">Qty</th>
                <th className="text-right py-3 px-4 text-slate-300">Cost</th>
                <th className="text-right py-3 px-4 text-slate-300">Price</th>
                <th className="text-right py-3 px-4 text-slate-300">Stock Value</th>
                <th className="text-right py-3 px-4 text-slate-300">Retail Value</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.products || []).slice(0, 100).map((p, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-white">{p.description}</td>
                  <td className="py-3 px-4 text-slate-400">{p.category}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{p.qty_remain}</td>
                  <td className="py-3 px-4 text-right text-slate-300">KES {p.cost_price?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-slate-300">KES {p.selling_price?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400">KES {p.stock_cost_value?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-blue-400">KES {p.stock_retail_value?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderStockMovementHistory = () => (
    <div className="space-y-6">
      <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-3 gap-4">
          <div><span className="text-slate-400">Total Received:</span> <span className="font-bold text-green-400">{reportData.summary?.total_received?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Total Sold:</span> <span className="font-bold text-red-400">{reportData.summary?.total_sold?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Net Movement:</span> <span className={`font-bold ${reportData.summary?.net_movement >= 0 ? 'text-green-400' : 'text-red-400'}`}>{reportData.summary?.net_movement?.toLocaleString()}</span></div>
        </div>
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Stock Movement History</h3>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-slate-800">
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">Date</th>
                <th className="text-left py-3 px-4 text-slate-300">Type</th>
                <th className="text-left py-3 px-4 text-slate-300">Product</th>
                <th className="text-right py-3 px-4 text-slate-300">Qty</th>
                <th className="text-right py-3 px-4 text-slate-300">Price</th>
                <th className="text-left py-3 px-4 text-slate-300">Reference</th>
                <th className="text-left py-3 px-4 text-slate-300">User</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.movements || []).map((m, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-slate-400">{m.date}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${m.movement_type === 'RECEIVE' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {m.movement_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-white">{m.description}</td>
                  <td className={`py-3 px-4 text-right font-semibold ${m.qty >= 0 ? 'text-green-400' : 'text-red-400'}`}>{m.qty > 0 ? '+' : ''}{m.qty}</td>
                  <td className="py-3 px-4 text-right text-slate-300">KES {m.unit_price?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-slate-400">{m.reference}</td>
                  <td className="py-3 px-4 text-slate-400">{m.user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderCategoryStock = () => (
    <div className="space-y-6">
      <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><span className="text-slate-400">Products:</span> <span className="font-bold text-white">{reportData.totals?.product_count?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Total Qty:</span> <span className="font-bold text-white">{reportData.totals?.total_qty?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Cost Value:</span> <span className="font-bold text-green-400">KES {reportData.totals?.cost_value?.toLocaleString()}</span></div>
          <div><span className="text-slate-400">Retail Value:</span> <span className="font-bold text-blue-400">KES {reportData.totals?.retail_value?.toLocaleString()}</span></div>
        </div>
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Stock by Category</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300">Category</th>
                <th className="text-right py-3 px-4 text-slate-300">Products</th>
                <th className="text-right py-3 px-4 text-slate-300">Total Qty</th>
                <th className="text-right py-3 px-4 text-slate-300">Cost Value</th>
                <th className="text-right py-3 px-4 text-slate-300">Retail Value</th>
                <th className="text-right py-3 px-4 text-slate-300">Potential Profit</th>
                <th className="text-right py-3 px-4 text-slate-300">Low Stock</th>
                <th className="text-right py-3 px-4 text-slate-300">Out of Stock</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.categories || []).map((c, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4 text-white font-medium">{c.category}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{c.product_count}</td>
                  <td className="py-3 px-4 text-right text-slate-300">{c.total_qty?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400">KES {c.stock_cost_value?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-blue-400">KES {c.stock_retail_value?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-purple-400">KES {c.potential_profit?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-yellow-400">{c.low_stock_count}</td>
                  <td className="py-3 px-4 text-right text-red-400">{c.out_of_stock_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderProfitReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard title="Total Revenue" value={`KES ${reportData.summary?.total_revenue?.toLocaleString() || 0}`} icon={DollarSign} color="blue" subtitle="Gross sales" />
        <StatCard title="Total Profit" value={`KES ${reportData.summary?.total_profit?.toLocaleString() || 0}`} icon={TrendingUp} color="green" subtitle="Net profit" />
        <StatCard title="Profit Margin" value={`${reportData.summary?.total_revenue ? ((reportData.summary?.total_profit / reportData.summary?.total_revenue) * 100).toFixed(1) : 0}%`} icon={BarChart3} color="purple" subtitle="Overall margin" />
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Most Profitable Products</h3>
        <div className="space-y-3">
          {(reportData.top_profitable_products || []).map((p, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 bg-green-500/20 text-green-400 rounded-lg flex items-center justify-center font-semibold">
                  {i + 1}
                </span>
                <div>
                  <p className="text-white font-medium">{p.description}</p>
                  <p className="text-sm text-slate-400">{p.total_qty} units · Revenue: KES {p.total_revenue?.toLocaleString()}</p>
                </div>
              </div>
              <span className="text-green-400 font-semibold">+KES {p.total_profit?.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderReportContent = () => {
    if (!reportData) return null;
    
    switch (activeReport) {
      case 'sales': return renderSalesReport();
      case 'daily-sales-summary': return renderDailySalesSummary();
      case 'products-sold': return renderProductsSold();
      case 'sales-by-payment-mode': return renderSalesByPaymentMode();
      case 'sales-by-customer': return renderSalesByCustomer();
      case 'top-selling-products': return renderTopSellingProducts();
      case 'user-performance': return renderUserPerformance();
      case 'user-activity': return renderUserActivity();
      case 'inventory': return renderInventoryReport();
      case 'stock-valuation': return renderStockValuation();
      case 'stock-movement-history': return renderStockMovementHistory();
      case 'category-stock': return renderCategoryStock();
      case 'profit': return renderProfitReport();
      default: return <p className="text-slate-400">Select a report to view</p>;
    }
  };

  const groupedReports = reports.reduce((acc, report) => {
    if (!acc[report.category]) acc[report.category] = [];
    acc[report.category].push(report);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Reports</h1>
        <p className="text-slate-400">Comprehensive business analytics and printable reports</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Report Selector */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Select Report</h3>
          <div className="space-y-4">
            {Object.entries(groupedReports).map(([category, categoryReports]) => (
              <div key={category}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{category}</p>
                <div className="space-y-1">
                  {categoryReports.map((report) => {
                    const Icon = report.icon;
                    return (
                      <button
                        key={report.id}
                        onClick={() => setActiveReport(report.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left ${
                          activeReport === report.id
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{report.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Report Content */}
        <div className="lg:col-span-3">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{reports.find(r => r.id === activeReport)?.name}</h2>
            </div>
            
            {renderFilters()}
            
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : (
              <div id="report-content">
                <h1 style={{display: 'none'}}>{reports.find(r => r.id === activeReport)?.name}</h1>
                {renderReportContent()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/sales" element={<SalesPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/suppliers" element={<SuppliersPage />} />
                    <Route path="/inventory" element={<InventoryPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                  </Routes>
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
export default App;
